<template>
    <div>
        <label class="image-label">{{ label }}</label>
        <div class="image-upload-container">
            <div class="image-upload-box">
                <input type="file" ref="fileInput" accept="image/*" multiple style="display: none" @change="onFilesChange" />
                <div class="image-upload-content" @click="abrirFileInput">
                    <span class="image-name">
                        {{ imagens.length === 0 ? 'Selecionar imagem...' : imagens.length + ' imagem(ns) selecionada(s)' }}
                    </span>
                    <button type="button" class="image-upload-btn" tabindex="-1" style="background: none; border: none; padding: 0; margin-left: 8px">
                        <span class="material-symbols-outlined" style="font-size: 20px; color: #0f97c7">upload</span>
                    </button>
                </div>
            </div>
            <div v-if="imagens.length > 0" class="image-preview-list">
                <div v-for="(img, idx) in imagens" :key="idx" class="image-preview-item">
                    <img :src="img.src" :alt="img.nome" class="image-preview-thumb" />
                    <span class="image-preview-name">{{ img.nome }}</span>
                    <Button icon="pi pi-trash" class="p-button-text image-remove-btn" @click="removerImagem(idx)" title="Remover imagem" />
                </div>
            </div>
        </div>

        <div class="p-fluid formgrid grid">
            <div class="field col-12">
                <label for="width">Largura</label>
                <div class="flex align-items-center">
                    <InputText id="width" type="number" v-model="widthValue" style="width: 70%" />
                    <Dropdown v-model="widthUnit" :options="unidades" optionLabel="label" optionValue="value" style="width: 30%; margin-left: 0.5rem" />
                </div>
            </div>
            <div class="field col-12">
                <div class="flex align-items-center">
                    <Checkbox id="backgroundTransparente" v-model="fundoTransparente" :binary="true" />
                    <label for="backgroundTransparente" class="ml-2">Fundo transparente</label>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';

const props = defineProps({
    modelValue: {
        type: Object,
        default: () => ({
            imagens: [],
            width: 100,
            fundoTransparente: false,
        }),
    },
    label: {
        type: String,
        default: 'Selecionar Imagem',
    },
});
const emit = defineEmits(['update:modelValue']);

const imagens = ref(props.modelValue.imagens);
const widthValue = ref(100);
const widthUnit = ref('%');
const fundoTransparente = ref(props.modelValue.fundoTransparente);
const fileInput = ref(null);

const unidades = ref([
    { label: 'px', value: 'px' },
    { label: '%', value: '%' },
]);

watch(
    () => props.modelValue,
    (val) => {
        imagens.value = val.imagens;

        const currentWidth = val.width;
        if (typeof currentWidth === 'string') {
            const value = parseInt(currentWidth, 10);
            const unit = currentWidth.match(/px|%/)?.[0] || '%';

            if (widthValue.value !== value) {
                widthValue.value = value;
            }
            if (widthUnit.value !== unit) {
                widthUnit.value = unit;
            }
        } else if (typeof currentWidth === 'number') {
            if (widthValue.value !== currentWidth) {
                widthValue.value = currentWidth;
            }
            if (widthUnit.value !== '%') {
                widthUnit.value = '%';
            }
        }

        if (fundoTransparente.value !== val.fundoTransparente) {
            fundoTransparente.value = val.fundoTransparente;
        }
    },
    { deep: true, immediate: true }
);

watch([widthValue, widthUnit, fundoTransparente], ([newWidthValue, newWidthUnit, newFundoTransparente]) => {
    let correctedVal = newWidthValue;
    if (newWidthUnit === '%' && newWidthValue > 100) {
        correctedVal = 100;
        widthValue.value = 100;
    }
    const combinedWidth = `${correctedVal}${newWidthUnit}`;

    if (combinedWidth !== props.modelValue.width || newFundoTransparente !== props.modelValue.fundoTransparente) {
        emit('update:modelValue', {
            ...props.modelValue,
            width: combinedWidth,
            fundoTransparente: newFundoTransparente,
        });
    }
});

function abrirFileInput() {
    if (fileInput.value) fileInput.value.click();
}

function onFilesChange(event) {
    const files = Array.from(event.target.files);
    const novasImagens = files.map((file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                resolve({
                    nome: file.name,
                    src: e.target.result,
                });
            };
            reader.readAsDataURL(file);
        });
    });
    Promise.all(novasImagens).then((results) => {
        const updatedImagens = [...imagens.value, ...results];
        emit('update:modelValue', { ...props.modelValue, imagens: updatedImagens });
    });
    event.target.value = '';
}

function removerImagem(idx) {
    const updatedImagens = [...imagens.value];
    updatedImagens.splice(idx, 1);
    emit('update:modelValue', { ...props.modelValue, imagens: updatedImagens });
}
</script>

<style scoped>
.image-label {
    display: block;
    font-weight: 500;
    margin-bottom: 6px;
}
.image-upload-container {
    margin-bottom: 8px;
}
.image-upload-box {
    position: relative;
    width: 100%;
}
.image-upload-content {
    display: flex;
    align-items: center;
    background: #f8f9fa;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    padding: 8px 12px;
    cursor: pointer;
    transition: border 0.2s;
}
.image-upload-content:hover {
    border-color: #0f97c7;
    background: #f0f8ff;
}
.image-name {
    flex: 1;
    color: #666;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.image-upload-icon {
    color: #0f97c7;
    font-size: 18px;
    margin-left: 8px;
}
.image-preview-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}
.image-preview-item {
    display: flex;
    align-items: center;
    background: #f8f9fa;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    padding: 4px 8px;
    gap: 8px;
}
.image-preview-thumb {
    width: 32px;
    height: 32px;
    object-fit: cover;
    border-radius: 2px;
    border: 1px solid #e5e7eb;
}
.image-preview-name {
    font-size: 13px;
    color: #444;
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    border-bottom: 1px dashed #0f97c7;
}
.image-remove-btn {
    margin-left: 4px;
}
.image-upload-btn {
    background: none;
    border: none;
    color: #0f97c7;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.image-upload-btn:hover {
    background: rgba(15, 151, 199, 0.1);
}
.material-symbols-outlined {
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
    font-size: 20px;
}
</style>
